/// <reference types="vite/client" />
// The portal Svelte flows' copy. English is bundled from ./en/*.json (each file is a namespace: the key
// 'returns.items.title' is `items.title` in en/returns.json) and is the source the server translates
// (PortalCatalog reads the same files). For another language the page passes the translated catalog in its
// initial data, and setPortalTranslations installs it before the flow mounts.

type Catalog = Record<string, string>
type Vars = Record<string, string | number>

const modules = import.meta.glob<{ default: Record<string, unknown> }>('./en/*.json', { eager: true })

function flatten(tree: unknown, prefix: string, into: Catalog): Catalog {
  if (tree && typeof tree === 'object') {
    for (const [key, value] of Object.entries(tree)) flatten(value, `${prefix}.${key}`, into)
  } else {
    into[prefix] = String(tree)
  }
  return into
}

const english: Catalog = Object.entries(modules).reduce<Catalog>((catalog, [path, { default: tree }]) => {
  const namespace = path.replace(/^.*\/(.+)\.json$/, '$1')
  return flatten(tree, namespace, catalog)
}, {})

let translations: Catalog = {}

// The page's language: the portal locale the server put on <html lang>.
export function pageLocale(): string {
  return document.documentElement.lang || 'en'
}

export function setPortalTranslations(next: Catalog | undefined): void {
  translations = next ?? {}
}

// With a numeric `count`, the key's `one` form for exactly 1 and its `other` form otherwise: the rule
// PortalCatalog#plural_key uses, so Rails and Svelte copy on the same page agree.
function pluralKey(key: string, count: number): string {
  const one = `${key}.one`
  return count === 1 && one in english ? one : `${key}.other`
}

function placeholders(text: string): string {
  return [...new Set(Array.from(text.matchAll(/\{(\w+)\}/g), (match) => match[1]))].sort().join(',')
}

// The copy for `key`, with `{name}` placeholders filled from `vars`. A translation whose placeholders differ
// from the English (a dropped {count}, an invented {name}) is not used: the English is shown instead.
export function t(key: string, vars: Vars = {}): string {
  const resolved = typeof vars.count === 'number' ? pluralKey(key, vars.count) : key
  const source = english[resolved]
  if (source === undefined) {
    console.warn(`Missing portal translation: ${resolved}`)
    return resolved
  }
  const translation = translations[resolved]
  const template = translation && placeholders(translation) === placeholders(source) ? translation : source
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match))
}
